/**
 * Relational Hub - Main landing page for the Relational Harmony journey
 * Phase 1: Self-connection focus with exercises library
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { 
  User, Heart, Users, Settings, Sparkles, 
  ArrowRight, Lock, Crown, BookOpen, Target,
  Compass, Brain, Shield, MessageCircle, Zap,
  CheckCircle2, Circle, ChevronRight
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { SelfConnectionExercises } from '@/components/relational/SelfConnectionExercises';
import { RelationshipExercises } from '@/components/relational/RelationshipExercises';
import { RelationalOnboardingModal } from '@/components/relational/RelationalOnboardingModal';
import { PageHeader } from '@/components/PageHeader';

interface RelationalFocus {
  connectionType: 'self' | 'partner' | 'both';
  relationshipType?: 'romantic' | 'family' | 'friendship' | 'work' | 'other';
  workingMode?: 'solo' | 'together';
  [key: string]: string | undefined;
}

const RelationalHub: React.FC = () => {
  const navigate = useNavigate();
  const { hasPremiumAccess } = useSubscription();
  
  const [relationalFocus, setRelationalFocus] = useState<RelationalFocus | null>(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('self');

  // Fetch user's relational focus from profile
  useEffect(() => {
    const fetchRelationalFocus = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      const { data: profile } = await supabase
        .from('profiles')
        .select('relational_focus')
        .eq('id', user.id)
        .single();

      if (profile?.relational_focus) {
        const focus = profile.relational_focus as RelationalFocus;
        setRelationalFocus(focus);
        // Set initial tab based on focus
        if (focus.connectionType === 'partner') {
          setActiveTab('relationships');
        } else {
          setActiveTab('self');
        }
      } else {
        // Show onboarding if no focus set
        setShowOnboarding(true);
      }
      
      setIsLoading(false);
    };

    fetchRelationalFocus();
  }, []);

  const handleOnboardingComplete = async (focus: RelationalFocus) => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    await supabase
      .from('profiles')
      .update({ relational_focus: focus })
      .eq('id', user.id);

    setRelationalFocus(focus);
    setShowOnboarding(false);
    
    if (focus.connectionType === 'partner') {
      setActiveTab('relationships');
    }
  };

  const showSelfTab = relationalFocus?.connectionType === 'self' || relationalFocus?.connectionType === 'both' || !relationalFocus;
  const showRelationshipsTab = relationalFocus?.connectionType === 'partner' || relationalFocus?.connectionType === 'both';

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <PageHeader
        title="Relational Harmony"
        showBackButton
        backTo="/"
      />

      <main className="container max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Key Message Card */}
        <Card className="bg-gradient-to-br from-primary/10 via-secondary/5 to-background border-primary/20">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="p-3 rounded-xl bg-primary/20">
                <Heart className="w-6 h-6 text-primary" />
              </div>
              <div className="space-y-2">
                <h3 className="font-semibold text-foreground">The Foundation of Connection</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  You can only be as connected to another person to the degree that you are connected with yourself. 
                  True relational harmony begins within - when we understand, accept, and nurture ourselves, 
                  we naturally create space for deeper, more authentic connections with others.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Current Focus Badge */}
        {relationalFocus && (
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="gap-1">
                {relationalFocus.connectionType === 'self' && <User className="w-3 h-3" />}
                {relationalFocus.connectionType === 'partner' && <Heart className="w-3 h-3" />}
                {relationalFocus.connectionType === 'both' && <Users className="w-3 h-3" />}
                {relationalFocus.connectionType === 'self' && 'Self Connection'}
                {relationalFocus.connectionType === 'partner' && 'Relationship Focus'}
                {relationalFocus.connectionType === 'both' && 'Self & Relationships'}
              </Badge>
              {relationalFocus.relationshipType && (
                <Badge variant="outline" className="text-xs">
                  {relationalFocus.relationshipType.charAt(0).toUpperCase() + relationalFocus.relationshipType.slice(1)}
                </Badge>
              )}
            </div>
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => setShowOnboarding(true)}
              className="text-xs"
            >
              <Settings className="w-3 h-3 mr-1" />
              Change Focus
            </Button>
          </div>
        )}

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full grid-cols-2">
            {showSelfTab && (
              <TabsTrigger value="self" className="gap-2">
                <User className="w-4 h-4" />
                Self Connection
              </TabsTrigger>
            )}
            {showRelationshipsTab && (
              <TabsTrigger value="relationships" className="gap-2">
                <Heart className="w-4 h-4" />
                Relationships
              </TabsTrigger>
            )}
            {!showSelfTab && <div />}
            {!showRelationshipsTab && <div />}
          </TabsList>

          {/* Self Connection Tab */}
          <TabsContent value="self" className="space-y-6">
            <SelfConnectionExercises />
          </TabsContent>

          {/* Relationships Tab */}
          <TabsContent value="relationships" className="space-y-6">
            <RelationshipExercises 
              relationshipType={relationalFocus?.relationshipType || 'romantic'}
              workingMode={relationalFocus?.workingMode || 'solo'}
            />
          </TabsContent>
        </Tabs>
      </main>

      {/* Onboarding Modal */}
      <RelationalOnboardingModal 
        open={showOnboarding}
        onOpenChange={setShowOnboarding}
        onComplete={handleOnboardingComplete}
        initialFocus={relationalFocus}
      />
    </div>
  );
};

export default RelationalHub;
